import { listRewardWheels, getRewardWheel, precheckRewardWheel, pullRewardWheel, getRewardWheelHistory } from './middleware-api.js';
import { getToken } from './middleware-config.js';
import { requireLogin } from './account-login-modal.js';
import { renderPagination } from './middleware-ui.js';

const EXTRA_SPINS = 5;
const BULB_COUNT = 24; // decorative rim bulbs (independent of prize count)
const FALLBACK_SEGMENT_COLORS = ['#a06828', '#f0ddc2']; // used only if the CSS variables can't be read

const ELIGIBILITY_MESSAGES = {
  GLOBAL_PULL_LIMIT_REACHED: 'This reward wheel has reached its total spin limit.',
  USER_PULL_LIMIT_REACHED: "You've used all your spins for this wheel.",
  SUBSLOT_DAY_NOT_OPEN: "This wheel isn't open for spins today.",
  SUBSLOT_PULL_LIMIT_REACHED: "You've used today's spin.",
  REQUIRE_FIRST_ORDER: 'Complete your first order to unlock this spin.',
  REQUIRE_MIN_BALANCED_POINTS: "You don't have enough points to spin this wheel yet.",
  REQUIRE_MIN_LIFETIME_POINTS: "You don't have enough lifetime points to spin this wheel yet.",
  WHEEL_NOT_STARTED: "This spin wheel hasn't started yet.",
  WHEEL_ENDED: 'This spin wheel has ended.',
  WHEEL_NOT_VISIBLE: 'This spin wheel is not currently available.',
  WHEEL_NO_ENTRIES_CONFIGURED: 'This spin wheel has no prizes configured yet.',
};

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.spin-to-win-root').forEach(init);
});

async function init(root) {
  const configuredWheelId = root.dataset.wheelId;
  const wheelId = configuredWheelId || (await resolveActiveWheelId(root));
  if (!wheelId) return;
  loadWheel(root, wheelId);
}

/**
 * Falls back to the current active wheel (the most recently created visible one)
 * when the section's "Reward wheel ID" setting is left blank.
 */
async function resolveActiveWheelId(root) {
  let list;
  try {
    list = await listRewardWheels({ limit: 1 });
  } catch (err) {
    root.innerHTML = `<p class="spin-to-win-error">${escapeHtml(err.message || 'Unable to load a reward wheel.')}</p>`;
    return null;
  }

  const wheel = list.rewardWheels?.[0];
  if (!wheel) {
    root.innerHTML = '<p class="spin-to-win-empty">There\'s no active reward wheel right now — check back soon!</p>';
    return null;
  }

  return wheel.documentId;
}

async function loadWheel(root, wheelId) {
  let detail;
  try {
    detail = await getRewardWheel(wheelId);
  } catch (err) {
    root.innerHTML = `<p class="spin-to-win-error">${escapeHtml(err.message || 'Unable to load this reward wheel.')}</p>`;
    return;
  }

  renderWheelShell(root, detail);

  const state = { wheelId, entries: detail.entries || [], spinning: false, currentRotation: 0 };
  const ring = root.querySelector('.spin-wheel__ring');
  const spinButton = root.querySelector('.spin-to-win-spin-button');
  const statusEl = root.querySelector('.spin-to-win-status');

  async function refreshEligibility() {
    if (!getToken()) {
      spinButton.disabled = false;
      setButtonLabel(spinButton, 'Log In to Spin');
      statusEl.textContent = '';
      return;
    }

    try {
      const eligibility = await precheckRewardWheel(wheelId);
      applyEligibility(spinButton, statusEl, eligibility);
    } catch (err) {
      spinButton.disabled = true;
      setButtonLabel(spinButton, 'No More Spins');
      statusEl.textContent = err.message || 'Unable to check spin eligibility.';
    }
  }

  spinButton.addEventListener('click', () => {
    if (state.spinning) return;
    if (!getToken()) {
      requireLogin(() => doPull(state, ring, spinButton, statusEl, refreshEligibility, () => refreshHistoryIfOpen(wheelId)));
      return;
    }
    doPull(state, ring, spinButton, statusEl, refreshEligibility, () => refreshHistoryIfOpen(wheelId));
  });

  const historyTrigger = root.querySelector('.spin-to-win-history-trigger');
  if (historyTrigger) {
    historyTrigger.addEventListener('click', () => openHistoryModal(wheelId));
  }

  await refreshEligibility();
}

function renderWheelShell(root, detail) {
  const entries = detail.entries || [];
  const segmentAngle = entries.length ? 360 / entries.length : 0;
  const segmentColors = readSegmentColors(root);

  const gradientStops = entries
    .map((_, i) => {
      const from = i * segmentAngle;
      const to = from + segmentAngle;
      return `${segmentColors[i % 2]} ${from}deg ${to}deg`;
    })
    .join(', ');

  const labelsHtml = entries
    .map((entry, i) => {
      const midAngle = segmentAngle * i + segmentAngle / 2;
      const { left, top } = pointOnWheel(midAngle, 30);
      const textClass = i % 2 === 0 ? 'spin-wheel__segment-label--cocoa' : 'spin-wheel__segment-label--cream';
      return `
        <div class="spin-wheel__segment-label ${textClass}" style="left: ${left}%; top: ${top}%;">
          ${entry.icon
            ? `<img src="${escapeHtml(entry.icon.url)}" alt="" class="spin-wheel__segment-icon">`
            : `<span class="spin-wheel__segment-emoji" aria-hidden="true">${emojiForEntry(entry)}</span>`}
          <span>${escapeHtml(entry.label || entry.title || '')}</span>
        </div>`;
    })
    .join('');

  if (detail.banner) {
    root.style.backgroundImage = `url('${detail.banner.url.replace(/'/g, "%27")}')`;
  }

  const hubLetter = escapeHtml((detail.title || '★').trim().charAt(0).toUpperCase() || '★');

  root.innerHTML = `
    <div class="spin-to-win-banner">
      <h2 class="spin-to-win-title">${escapeHtml(detail.title || '')}</h2>
      ${detail.subtitle ? `<p class="spin-to-win-subtitle">${escapeHtml(detail.subtitle)}</p>` : ''}
    </div>

    <div class="spin-wheel-wrapper">
      <div class="spin-wheel-pointer">&nbsp;</div>
      <div class="spin-wheel">
        <div class="spin-wheel__ring" style="background: conic-gradient(${gradientStops}); transform: rotate(0deg); --stw-seg-angle: ${segmentAngle}deg;">
          ${labelsHtml}
        </div>
      </div>
      <div class="spin-wheel__hub">${hubLetter}</div>
    </div>

    <button type="button" class="spin-to-win-spin-button" disabled>
      <span class="spin-to-win-spin-button__label">Spin</span>
      <span class="spin-to-win-spin-button__sublabel"></span>
    </button>
    <p class="spin-to-win-status" role="status"></p>

    ${detail.description ? `<p class="spin-to-win-description">${escapeHtml(detail.description)}</p>` : ''}

    <div class="spin-to-win-actions">
      ${detail.termsAndConditions ? '<button type="button" class="spin-to-win-terms-trigger">Terms &amp; Conditions</button>' : ''}
      <button type="button" class="spin-to-win-history-trigger">My Spin History</button>
    </div>
  `;

  renderBulbs(root.querySelector('.spin-wheel-wrapper'));

  const termsTrigger = root.querySelector('.spin-to-win-terms-trigger');
  if (termsTrigger) {
    termsTrigger.addEventListener('click', () => showTermsModal(detail.termsAndConditions));
  }
}

/**
 * Adds a ring of decorative glowing bulbs around the wheel rim. The count is
 * fixed (independent of the number of prizes) and evenly spaced; the staggered
 * animation-delay makes them appear to chase. Placed on the (non-rotating)
 * wrapper so they stay still while the wheel spins.
 */
function renderBulbs(wrapper) {
  if (!wrapper) return;
  for (let i = 0; i < BULB_COUNT; i++) {
    const { left, top } = pointOnWheel((360 / BULB_COUNT) * i, 49);
    const bulb = document.createElement('span');
    bulb.className = 'spin-wheel__bulb';
    bulb.setAttribute('aria-hidden', 'true');
    bulb.style.left = `${left}%`;
    bulb.style.top = `${top}%`;
    bulb.style.animationDelay = `${(i % 8) * 0.12}s`;
    wrapper.appendChild(bulb);
  }
}

function showTermsModal(terms) {
  let modal = document.getElementById('spin-to-win-terms-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'spin-to-win-terms-modal';
    modal.className = 'popup-modal hidden';
    document.body.appendChild(modal);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.classList.add('hidden');
    });
  }

  modal.innerHTML = `
    <div class="popup-content white-box spin-to-win-terms-modal-content">
      <button type="button" class="close-popup" aria-label="Close">&times;</button>
      <h3>Terms &amp; Conditions</h3>
      <div class="spin-to-win-terms-body">${escapeHtml(terms)}</div>
    </div>
  `;
  modal.querySelector('.close-popup').addEventListener('click', () => modal.classList.add('hidden'));
  modal.classList.remove('hidden');
}

function openHistoryModal(wheelId) {
  let modal = document.getElementById('spin-to-win-history-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'spin-to-win-history-modal';
    modal.className = 'popup-modal hidden';
    document.body.appendChild(modal);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.classList.add('hidden');
    });
  }

  modal.innerHTML = `
    <div class="popup-content white-box spin-to-win-history-modal-content">
      <button type="button" class="close-popup" aria-label="Close">&times;</button>
      <h3 class="spin-to-win-history-modal-title">My Spin History</h3>
      <div class="customer account">
        <div class="spin-to-win-history-list"></div>
        <div class="spin-to-win-history-pagination pagination" id="spin-to-win-history-pagination"></div>
      </div>
    </div>
  `;
  modal.querySelector('.close-popup').addEventListener('click', () => modal.classList.add('hidden'));
  modal.classList.remove('hidden');

  loadHistory(modal, wheelId, 0);
}

function refreshHistoryIfOpen(wheelId) {
  const modal = document.getElementById('spin-to-win-history-modal');
  if (modal && !modal.classList.contains('hidden')) {
    loadHistory(modal, wheelId, 0);
  }
}

function applyEligibility(spinButton, statusEl, eligibility) {
  if (eligibility.eligible) {
    spinButton.disabled = false;
    setButtonLabel(spinButton, 'Spin Now', formatRemainingSpins(eligibility));
    statusEl.textContent = '';
    return;
  }

  spinButton.disabled = true;
  setButtonLabel(spinButton, 'No More Spins');
  statusEl.textContent = messageForReason(eligibility);
}

function setButtonLabel(spinButton, label, sublabel = '') {
  spinButton.querySelector('.spin-to-win-spin-button__label').textContent = label;
  spinButton.querySelector('.spin-to-win-spin-button__sublabel').textContent = sublabel;
}

function messageForReason(eligibility) {
  const { reason, subslotSchedule } = eligibility;

  if (reason === 'SUBSLOT_PULL_LIMIT_REACHED' || reason === 'SUBSLOT_DAY_NOT_OPEN') {
    const next = subslotSchedule?.futureSubslots?.[0]?.start;
    if (next) {
      return `${ELIGIBILITY_MESSAGES[reason]} Next spin opens ${formatDate(next)}.`;
    }
  }

  return ELIGIBILITY_MESSAGES[reason] || "You're not eligible to spin this wheel right now.";
}

function formatRemainingSpins(eligibility) {
  const { userPullLimit, userPullCount, subslotSchedule } = eligibility;

  const lifetimeRemaining = userPullLimit == null ? null : Math.max(0, userPullLimit - (userPullCount || 0));
  const currentSubslot = subslotSchedule?.currentSubslot;
  const subslotRemaining = currentSubslot == null ? null : Math.max(0, currentSubslot.limit - (currentSubslot.count || 0));

  if (lifetimeRemaining == null && subslotRemaining == null) return '';

  // The wheel enforces the lifetime and daily caps independently, so what a
  // customer can actually still do right now is bounded by whichever is tighter.
  const remaining =
    subslotRemaining == null ? lifetimeRemaining : lifetimeRemaining == null ? subslotRemaining : Math.min(lifetimeRemaining, subslotRemaining);

  const boundByToday = subslotRemaining != null && subslotRemaining <= (lifetimeRemaining ?? Infinity);
  return `${remaining} spin${remaining === 1 ? '' : 's'} remaining${boundByToday ? ' today' : ''}.`;
}

async function doPull(state, ring, spinButton, statusEl, refreshEligibility, refreshHistory) {
  state.spinning = true;
  spinButton.disabled = true;
  setButtonLabel(spinButton, 'Spinning...');
  statusEl.textContent = '';

  let result;
  try {
    result = await pullRewardWheel(state.wheelId);
  } catch (err) {
    state.spinning = false;
    statusEl.textContent = '';
    showToast(err.message || 'Unable to spin right now.', 'error');
    refreshEligibility();
    return;
  }

  const targetRotation = computeTargetRotation(state.entries, result.entry.documentId, state.currentRotation);
  state.currentRotation = targetRotation;
  ring.style.transform = `rotate(${targetRotation}deg)`;

  ring.addEventListener(
    'transitionend',
    function onEnd() {
      ring.removeEventListener('transitionend', onEnd);
      state.spinning = false;
      statusEl.textContent = '';
      showResultModal(result, state.wheelId);
      refreshEligibility();
      refreshHistory();
    },
    { once: true }
  );
}

function computeTargetRotation(entries, wonEntryId, currentRotation) {
  if (!entries.length) return currentRotation + EXTRA_SPINS * 360;

  const segmentAngle = 360 / entries.length;
  const index = entries.findIndex((e) => e.documentId === wonEntryId);
  const targetIndex = index === -1 ? 0 : index;
  const midAngle = targetIndex * segmentAngle + segmentAngle / 2;

  const baseline = currentRotation - (currentRotation % 360);
  return baseline + EXTRA_SPINS * 360 + (360 - midAngle);
}

function showResultModal(result, wheelId) {
  const { entry, draw, userVouchers } = result;
  const isPoints = entry.rewardType === 'points';

  const rewardIconHtml = entry.icon
    ? `<img src="${escapeHtml(entry.icon.url)}" alt="" class="spin-to-win-result__reward-icon">`
    : `<span class="spin-to-win-result__star" aria-hidden="true">${isPoints ? '⭐' : '🎁'}</span>`;

  const rewardHtml = isPoints
    ? `<p class="spin-to-win-result__points">+${escapeHtml(draw.pointsAmount)} <span class="spin-to-win-result__points-unit">points</span></p>`
    : (userVouchers || [])
        .map(
          (v) => `
        <div class="spin-to-win-result__voucher">
          <p class="spin-to-win-result__voucher-code">${escapeHtml(v.voucherCode)}</p>
          ${v.voucherName ? `<p>${escapeHtml(v.voucherName)}</p>` : ''}
        </div>`
        )
        .join('');

  let modal = document.getElementById('spin-to-win-result-modal');
  if (!modal) {
    modal = document.createElement('div');
    modal.id = 'spin-to-win-result-modal';
    modal.className = 'popup-modal hidden';
    document.body.appendChild(modal);
    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.classList.add('hidden');
    });
  }

  modal.innerHTML = `
    <div class="popup-content white-box spin-to-win-result">
      <button type="button" class="close-popup" aria-label="Close">&times;</button>
      <div class="spin-to-win-result__gift" aria-hidden="true">🎁</div>
      <h3 class="spin-to-win-result__headline">Woohoo!</h3>
      <p class="spin-to-win-result__congrats">${escapeHtml(entry.title || 'Congratulations!')}</p>
      ${entry.label && entry.label !== entry.title ? `<p class="spin-to-win-result__prize-name">${escapeHtml(entry.label)}</p>` : ''}
      <p class="spin-to-win-result__lead">You have earned</p>
      <div class="spin-to-win-result__reward-box">
        ${rewardIconHtml}
        ${rewardHtml}
        <p class="spin-to-win-result__reward-caption">${escapeHtml(entry.subtitle || 'Added to your account')}</p>
      </div>
      ${entry.description ? `<p class="spin-to-win-result__hint">${escapeHtml(entry.description)}</p>` : ''}
      <button type="button" class="spin-to-win-result__cta">Yay! Continue Shopping <span aria-hidden="true">→</span></button>
      <button type="button" class="spin-to-win-result__history-link">My Spin History</button>
    </div>
  `;

  // .spin-to-win-result__cta intentionally isn't reusing the shared .close-popup
  // class here — that class carries alpro.css's small absolute-positioned circular
  // button styling, which would collide with this button's own full-width layout.
  modal.querySelector('.close-popup').addEventListener('click', () => modal.classList.add('hidden'));
  modal.querySelector('.spin-to-win-result__cta').addEventListener('click', () => {
    window.location.href = 'https://alpropharmacy.com.my/pages/oneclick';
  });
  modal.querySelector('.spin-to-win-result__history-link').addEventListener('click', () => {
    modal.classList.add('hidden');
    openHistoryModal(wheelId);
  });
  modal.classList.remove('hidden');
  burstConfetti(modal.querySelector('.popup-content'));
}

async function loadHistory(modal, wheelId, start = 0) {
  const listEl = modal.querySelector('.spin-to-win-history-list');
  const paginationEl = modal.querySelector('.spin-to-win-history-pagination');
  if (!listEl) return;

  if (!getToken()) {
    listEl.innerHTML = '<p class="spin-to-win-history-empty">Log in to see your spin history.</p>';
    if (paginationEl) paginationEl.innerHTML = '';
    return;
  }

  listEl.innerHTML = '<p class="spin-to-win-history-loading">Loading…</p>';
  if (paginationEl) paginationEl.innerHTML = '';

  let history;
  try {
    history = await getRewardWheelHistory({ rewardWheelDocumentId: wheelId, limit: 10, start });
  } catch (err) {
    listEl.innerHTML = `<p class="spin-to-win-history-error">${escapeHtml(err.message || 'Unable to load your spin history.')}</p>`;
    return;
  }

  if (!history.draws.length) {
    listEl.innerHTML = '<p class="spin-to-win-history-empty">No spins yet — give it a try!</p>';
    return;
  }

  const rows = history.draws
    .map((draw) => {
      const codes = (draw.entry?.vouchers || []).map((v) => escapeHtml(v.voucherCode)).join(', ');
      const reward = draw.pointsAmount != null ? `+${escapeHtml(draw.pointsAmount)} pts` : codes || '—';
      return `
        <tr role="row">
          <td role="cell" data-label="Prize">${escapeHtml(draw.entry?.label || draw.entry?.title || 'Prize')}</td>
          <td role="cell" data-label="Date">${formatDate(draw.createdAt)}</td>
          <td role="cell" data-label="Reward">${reward}</td>
        </tr>`;
    })
    .join('');

  listEl.innerHTML = `
    <table role="table" class="order-history">
      <caption class="visually-hidden">My Spin History</caption>
      <thead role="rowgroup">
        <tr role="row">
          <th scope="col" role="columnheader">Prize</th>
          <th scope="col" role="columnheader">Date</th>
          <th scope="col" role="columnheader">Reward</th>
        </tr>
      </thead>
      <tbody role="rowgroup">${rows}</tbody>
    </table>
  `;

  if (paginationEl) {
    if (!paginationEl.id) paginationEl.id = 'spin-to-win-history-pagination';
    renderPagination(paginationEl.id, history.pagination, (newStart) => loadHistory(modal, wheelId, newStart));
  }
}

/**
 * Converts a wheel angle (0deg = top, clockwise, matching the conic-gradient
 * segments) plus a radius (as % of the wheel's size) into a {left, top}
 * position (also as %), so labels sit at a fixed distance from the hub
 * regardless of each label's own content height.
 */
/**
 * Reads the actual --stw-gold / --stw-light-cocoa custom property values
 * (set by the section's color settings in sections/spin-to-win.liquid) so the
 * JS-generated conic-gradient wedges stay in sync with those settings, rather
 * than a hardcoded color pair that can never be reconfigured.
 */
function readSegmentColors(root) {
  const styles = getComputedStyle(root);
  const gold = styles.getPropertyValue('--stw-gold').trim();
  const lightCocoa = styles.getPropertyValue('--stw-light-cocoa').trim();
  return [gold || FALLBACK_SEGMENT_COLORS[0], lightCocoa || FALLBACK_SEGMENT_COLORS[1]];
}

function pointOnWheel(angleDeg, radiusPercent) {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    left: 50 + radiusPercent * Math.sin(rad),
    top: 50 - radiusPercent * Math.cos(rad),
  };
}

function formatDate(isoString) {
  if (!isoString) return '';
  return new Date(isoString).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Picks a fallback emoji for a prize when the entry has no uploaded icon.
 * Keyed off rewardType first, then a few label keywords. Emoji render
 * differently per OS — swap for uploaded icons (entry.icon) for a consistent
 * look across devices.
 */
function emojiForEntry(entry) {
  const type = (entry.rewardType || '').toLowerCase();
  const label = (entry.label || entry.title || '').toLowerCase();

  if (label.includes('sorry') || label.includes('better luck') || label.includes('try again')) return '😊';
  if (type === 'points') return '🪙';
  if (type === 'voucher') {
    if (label.includes('ship')) return '🚚';
    if (label.includes('2') || label.includes('vouchers')) return '🎁';
    return '🎟️';
  }
  return '🎉';
}

/**
 * Lightweight canvas confetti burst inside the given container (no library,
 * CSP-safe). Skips when the user prefers reduced motion. The canvas removes
 * itself once all particles fade.
 */
function burstConfetti(container) {
  if (!container || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const canvas = document.createElement('canvas');
  const rect = container.getBoundingClientRect();
  canvas.width = rect.width || 320;
  canvas.height = rect.height || 240;
  Object.assign(canvas.style, { position: 'absolute', inset: '0', pointerEvents: 'none', zIndex: '5' });
  if (getComputedStyle(container).position === 'static') container.style.position = 'relative';
  container.appendChild(canvas);

  const ctx = canvas.getContext('2d');
  const colors = ['#1E4284', '#F05A27', '#f6c445', '#ffffff'];
  const parts = Array.from({ length: 80 }, () => ({
    x: canvas.width / 2,
    y: canvas.height * 0.3,
    vx: (Math.random() - 0.5) * 8,
    vy: Math.random() * -7 - 2,
    g: 0.24,
    s: 4 + Math.random() * 4,
    c: colors[Math.floor(Math.random() * colors.length)],
    a: 1,
    r: Math.random() * 6,
  }));

  (function frame() {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    let live = false;
    parts.forEach((p) => {
      p.vy += p.g;
      p.x += p.vx;
      p.y += p.vy;
      p.a -= 0.011;
      p.r += 0.2;
      if (p.a > 0) {
        live = true;
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.a);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.r);
        ctx.fillStyle = p.c;
        ctx.fillRect(-p.s / 2, -p.s / 2, p.s, p.s * 0.6);
        ctx.restore();
      }
    });
    if (live) requestAnimationFrame(frame);
    else canvas.remove();
  })();
}

function escapeHtml(value) {
  const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  return String(value ?? '').replace(/[&<>"']/g, (ch) => map[ch]);
}